// Phase 4 — Angular HttpClient port of the AngularJS `Access` factory
// (was sudo/access/Access.js, DELETED). Downgraded back to the AngularJS `Access` name
// (see downgrades.ts), so all 8 legacy consumers (role/permission/user controllers) use
// this single service. Every method returns a Promise (via firstValueFrom) so the existing
// `.then(...)` call sites keep working unchanged.
//
// Resolve semantics are preserved EXACTLY from the old factory:
//   - fetch* (GET list)      → the parsed body (old `resp.data`)
//   - create/update/assign/
//     revoke Role/Permission  → `true` (old `.then(() => true)`)
//   - role right / documentType assign/revoke → the parsed body (old `resp.data`)
// The `secret-key` header is added globally by secretKeyInterceptor — no per-call work here.
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AccessService {
    private http = inject(HttpClient);

    fetchPermissionList(type: string): Promise<any> {
        return firstValueFrom(this.http.get('/right/list/?type=' + type));
    }

    createPermission(params: any): Promise<boolean> {
        return firstValueFrom(
            this.http.post('/right/create', {
                name: params.name,
                description: params.description,
                type: params.type,
            }),
        ).then(() => true);
    }

    updatePermission(params: any): Promise<boolean> {
        return firstValueFrom(
            this.http.post('/right/update', {
                _id: params._id,
                name: params.name,
                description: params.description,
                type: params.type,
            }),
        ).then(() => true);
    }

    fetchRoleList(type: string): Promise<any> {
        return firstValueFrom(this.http.get('/role/list/?type=' + type));
    }

    createRole(params: any): Promise<boolean> {
        return firstValueFrom(
            this.http.post('/role/create', {
                name: params.name,
                description: params.description,
                type: params.type,
            }),
        ).then(() => true);
    }

    AssignRole(params: any): Promise<boolean> {
        return firstValueFrom(
            this.http.post('/role/assign', {
                userId: params.userId,
                roleId: params.roleId,
            }),
        ).then(() => true);
    }

    RevokeRole(params: any): Promise<boolean> {
        return firstValueFrom(
            this.http.post('/role/revoke', {
                userId: params.userId,
                roleId: params.roleId,
            }),
        ).then(() => true);
    }

    updateRole(params: any): Promise<boolean> {
        return firstValueFrom(
            this.http.post('/role/update', {
                _id: params._id,
                name: params.name,
                description: params.description,
                type: params.type,
            }),
        ).then(() => true);
    }

    roleAssignRight(params: any): Promise<any> {
        return firstValueFrom(
            this.http.post('/role/right/assign', {
                roleId: params.roleId,
                rightId: params.rightId,
            }),
        );
    }

    roleRevokeRight(params: any): Promise<any> {
        return firstValueFrom(
            this.http.post('/role/right/revoke', {
                roleId: params.roleId,
                rightId: params.rightId,
            }),
        );
    }

    // Relative URLs (no leading slash) preserved verbatim from the legacy factory.
    AssignDocumentType(params: any): Promise<any> {
        return firstValueFrom(
            this.http.post('role/documentType/assign', {
                roleId: params.roleId,
                documentTypeId: params.documentTypeId,
            }),
        );
    }

    RevokeDocumentType(params: any): Promise<any> {
        return firstValueFrom(
            this.http.post('role/documentType/revoke', {
                roleId: params.roleId,
                documentTypeId: params.documentTypeId,
            }),
        );
    }
}
